import Card from 'components/Layout/Card';
import Loader from 'components/Layout/Loader';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { icons } from 'utils/constants';
import TextInput from 'components/form/TextInput';
import Button from 'components/form/Button';
import TextArea from 'components/form/TextArea';
import {
  fetchAddOnServicesRequestData,
  handleAddOnServicesRequest,
  showSuccess,
} from 'store/slices';
import '../NewRegistrationEventForm.scss';
import { getDataFromLocalStorage, objectToFormData } from 'utils/helpers';
import { Table } from 'react-bootstrap';

const ViewNewEventAdOnServicesForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [addOnServicesData, setAddOnServicesData] = useState([]);
  const [allData, setAllData] = useState({});
  const { eventId, requestType, requestId } = params || {};
  const userData = getDataFromLocalStorage();

  const fetchAddOnServices = async () => {
    const response = await dispatch(
      fetchAddOnServicesRequestData({ request_id: requestId })
    );
    if (response?.status === 200) {
      setAllData(response?.data);
      setAddOnServicesData(response?.data?.service_data || []);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAddOnServices();
  }, [requestId]);

  const handleSubmit = async (values, status) => {
    setLoading(true);
    const payload = {
      event_id: eventId,
      request_id: requestId,
      request_status: status === 'approved' ? '1' : '2',
      service_data: JSON.stringify(values.services),
      comment: values.comment,
      paper_submission_service: values?.visibility?.paper_submission_service
        ? 1
        : 0,
      plag_revision_service: values?.visibility?.plag_revision_service ? 1 : 0,
      reviewer_revision_service: values?.visibility?.reviewer_revision_service
        ? 1
        : 0,
    };

    let forData = objectToFormData(payload);
    const response = await dispatch(handleAddOnServicesRequest(forData));

    if (response?.status === 200) {
      fetchAddOnServices();
      dispatch(showSuccess(response.message));
      // alert(`Request ${status} successfully`);
      // navigate(-1);
      setLoading(false);
    }
    setLoading(false);
  };

  const initialValues = {
    services: addOnServicesData?.length
      ? addOnServicesData?.map((service) => ({
          category: service.category || '',
          free_member: service.free_member ?? 0,
          premium_member: service.premium_member ?? 0,
          is_updated_category: service.is_updated_category ?? false,
          is_updated_free_membership: service.is_updated_free_membership ?? false,
          is_updated_premium_membership:
            service.is_updated_premium_membership ?? false,
        }))
      : [
          {
            category: '',
            free_member: '',
            premium_member: '',
          },
        ],
    comment: allData.comment ?? '',
    visibility: {
      paper_submission_service:
        allData?.paper_submission_service !== '0' ? true : false,
      plag_revision_service:
        allData?.plag_revision_service !== '0' ? true : false,
      reviewer_revision_service:
        allData?.reviewer_revision_service !== '0' ? true : false,
    },
  };
  const validationSchema = Yup.object().shape({
    services: Yup.array().of(
      Yup.object().shape({
        category: Yup.string().required('Add-on service is required'),
        free_member: Yup.number()
          .typeError('Must be a number')
          .required('Free Member is Required'),
        premium_member: Yup.number()
          .typeError('Must be a number')
          .required('Premium Member is Required'),
      })
    ),
  });

  return (
    <Card className={'cps-24 cpe-24 cpb-24 cmb-12'}>
      {isLoading ? (
        <div className="cpt-80 cpb-80">
          <Loader size="md" />
        </div>
      ) : (
        <div id="new-registration-event-form-block">
          <div className="d-flex gap-2 align-items-center mb-3 cpt-20">
            <div
              onClick={() => {
                navigate(-1);
              }}
            >
              <img
                src={icons.leftArrow}
                alt="back"
                className="h-21 me-3 pointer"
              />
            </div>

            <div className="text-18-500 color-black-olive">Add-On Services</div>
          </div>

          <div>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              // onSubmit={handleSave}
              validationSchema={validationSchema}
            >
              {({ values, errors, handleChange, setFieldValue }) => (
                <Form>
                  <FieldArray name="services">
                    {({ push, remove }) => {
                      return (
                        <>
                          {values.services.map((service, index) => (
                            <div key={index} className="row g-3 mb-3">
                              {/* Category Field */}
                              <div className="col-md-4 col-12">
                                <TextInput
                                  label="Category"
                                  placeholder="Category"
                                  id={`services.${index}.category`}
                                  value={values.services?.[index]?.category}
                                  // onChange={handleChange}
                                  onChange={handleChange}
                                  error={errors.services?.[index]?.category}
                                  isBadge={
                                    values.services?.[index]
                                      ?.is_updated_category
                                  }
                                  badgeStyle={{ left: '80px' }}
                                  disabled={
                                    allData?.request_status &&
                                    ['1', '2'].includes(allData.request_status)
                                  }
                                />
                              </div>

                              {/* Free Membership Field */}
                              <div className="col-md-3 col-12">
                                <TextInput
                                  label="Free Membership"
                                  placeholder="Free Membership"
                                  id={`services.${index}.free_member`}
                                  value={values.services?.[index]?.free_member}
                                  onChange={handleChange}
                                  error={errors.services?.[index]?.free_member}
                                  isBadge={
                                    values.services?.[index]
                                      ?.is_updated_free_membership
                                  }
                                  disabled={
                                    allData?.request_status &&
                                    ['1', '2'].includes(allData.request_status)
                                  }
                                />
                              </div>

                              {/* Premium Membership Field */}
                              <div className="col-md-3 col-12">
                                <TextInput
                                  label="Premium Membership"
                                  placeholder="Premium Membership"
                                  id={`services.${index}.premium_member`}
                                  value={
                                    values.services?.[index]?.premium_member
                                  }
                                  onChange={handleChange}
                                  error={
                                    errors.services?.[index]?.premium_member
                                  }
                                  isBadge={
                                    values.services?.[index]
                                      ?.is_updated_premium_membership
                                  }
                                  badgeStyle={{ left: '180px' }}
                                  disabled={
                                    allData?.request_status &&
                                    ['1', '2'].includes(allData.request_status)
                                  }
                                />
                              </div>

                              {/* Remove Button */}

                              {!['1', '2'].includes(allData.request_status) && (
                                <div className="col-md-2 d-flex align-items-center col-12">
                                  {values.services.length > 0 && (
                                    <Button
                                      icon={<i className="bi bi-trash fs-5" />}
                                      btnStyle="primary-outline"
                                      className="h-35 text-14-500 text-nowrap w-auto px-3"
                                      onClick={() => remove(index)}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      );
                    }}
                  </FieldArray>

                  {/* Visibility Configuration Table */}
                  <div
                    className="cmt-20"
                    style={{ maxWidth: '438px', overflowX: 'auto' }}
                  >
                    <Table bordered className="w-100">
                      <thead>
                        <tr>
                          <th className="text-center text-14-500-18 bg-E9EE">
                            Visibility Configuration
                          </th>
                          <th className="text-center text-14-500-18 bg-E9EE">
                            Visibility
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {Object.keys(values?.visibility).map((key, index) => (
                          <tr key={index}>
                            <td className="text-center text-14-400-18">
                              {key === 'paper_submission_service'
                                ? 'Paper Submission'
                                : key === 'plag_revision_service'
                                ? 'Plag Revision / Rejection'
                                : 'Reviewer Revision / Rejection'}
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                name={`visibility.${key}`}
                                checked={values?.visibility[key]}
                                onChange={(e) =>
                                  setFieldValue(
                                    `visibility.${key}`,
                                    e.target.checked
                                  )
                                }
                                disabled={
                                  allData?.request_status &&
                                  ['1', '2'].includes(allData.request_status)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div
                    className="cmt-20"
                    style={{ maxWidth: '800px', overflowX: 'auto' }}
                  >
                    <TextArea
                      label="Comment"
                      value={values.comment}
                      onChange={handleChange}
                      id="comment"
                      //   error={errors.description}
                      rows={3}
                      disabled={
                        allData?.request_status &&
                        ['1', '2'].includes(allData.request_status)
                      }
                    />
                  </div>

                  {/* Buttons Section */}

                  {allData?.request_status === '1' ? (
                    <div className="cmt-20 color-success">
                      {userData?.user_type === '6'
                        ? 'Your request has accepted.'
                        : 'You has accepted this request.'}
                    </div>
                  ) : allData?.request_status === '2' ? (
                    <div className="cmt-20 color-danger-bar">
                      {userData?.user_type === '6'
                        ? 'Your request has rejected.'
                        : 'You has rejected this request.'}
                    </div>
                  ) : (
                    <div className="mt-3 d-flex gap-3 cmt-20 align-items-center">
                      {/* Submit Button */}
                      <Button
                        btnStyle="primary-dark"
                        text="Approve Request"
                        className="h-35 text-14-500 text-nowrap w-auto px-3"
                        type="submit"
                        onClick={() => handleSubmit(values, 'approved')}
                        btnLoading={loading}
                        disabled={loading}
                      />

                      <Button
                        btnStyle="primary-outline"
                        text="Reject"
                        className="h-35 text-14-500 text-nowrap w-auto px-3"
                        type="submit"
                        onClick={() => handleSubmit(values, 'rejected')}
                        disabled={loading}
                      />

                      {/* Cancel Button */}
                      <Button
                        btnStyle="light-outline"
                        text="Cancel"
                        className="h-35 text-14-500 text-nowrap w-auto px-3"
                        type="button"
                        onClick={() => navigate(-1)}
                        disabled={loading}
                      />
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ViewNewEventAdOnServicesForm;
